import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  MdEmail,
  MdLock,
  MdVisibility,
  MdVisibilityOff,
  MdArrowForward,
  MdVerifiedUser,
  MdSpeed,
  MdHub,
  MdSecurity,
} from "react-icons/md";
import loginicon from "../assets/OG Logo.png";
import { createLogin } from "../apis/LoginApi";
import { brand } from "../theme";
import { useAuth } from "../context/AuthProvider";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { loginSession } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter both email/phone and password");
      return;
    }

    setLoading(true);
    try {
      const res = await createLogin({
        identifier: email,
        password: password,
      });

      if (res.success === true) {
        // ✅ Store cookie / token / user session in localStorage (crucial for Mac / Safari cross-origin)
        if (res.token) {
          localStorage.setItem("auth_token", res.token);
          localStorage.setItem("token", res.token);
        }
        if (res.user) {
          localStorage.setItem("user", JSON.stringify(res.user));
          if (res.user.role) {
            localStorage.setItem("role", res.user.role);
          }
        }

        window.__AUTH__ = {
          token: res.token,
          role: res.user?.role || "admin",
          user: res.user,
        };

        if (loginSession) {
          loginSession(res.user, res.token);
        }

        toast.success("Authentication successful. Welcome back!");

        setTimeout(() => {
          navigate("/", { replace: true });
        }, 300);
      } else {
        toast.error("Invalid login credentials");
      }
    } catch (err) {
      console.error("LOGIN ERROR 👉", err);
      toast.error(err?.response?.data?.error || "Invalid credentials or login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: "#05041A",
        position: "relative",
        overflow: "hidden",
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        // Keyframe Animations
        "@keyframes fadeIn": {
          "0%": { opacity: 0, transform: "scale(0.98) translateY(10px)" },
          "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
        },
        "@keyframes pulseGlow": {
          "0%, 100%": { opacity: 0.15 },
          "50%": { opacity: 0.3 },
        },
        "@keyframes floatSlow": {
          "0%, 100%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-6px)" },
        },
        // Ambient Lighting & Grid Background
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(ellipse at 20% 20%, rgba(201, 162, 39, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(11, 10, 61, 0.8) 0%, transparent 60%),
            linear-gradient(to right, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: "100% 100%, 100% 100%, 40px 40px, 40px 40px",
          pointerEvents: "none",
        },
      }}
    >
      {/* Main Split Container */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1040,
          minHeight: { md: 580 },
          borderRadius: "24px",
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          bgcolor: "rgba(11, 10, 61, 0.75)",
          backdropFilter: "blur(24px)",
          border: "1px solid rgba(201, 162, 39, 0.25)",
          boxShadow: "0 30px 80px -20px rgba(5, 4, 26, 0.95), 0 0 50px -10px rgba(201, 162, 39, 0.1)",
          animation: "fadeIn 600ms cubic-bezier(0.16, 1, 0.3, 1) forwards",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left Section: Brand Experience & Identity */}
        <Box
          sx={{
            flex: { md: 1.1 },
            bgcolor: "#070624",
            backgroundImage: `linear-gradient(160deg, #0B0A3D 0%, #05041A 100%)`,
            p: { xs: 4, sm: 5, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            borderRight: { md: "1px solid rgba(255, 255, 255, 0.08)" },
            borderBottom: { xs: "1px solid rgba(255, 255, 255, 0.08)", md: "none" },
            overflow: "hidden",
          }}
        >
          {/* Subtle Glow Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: "-30%",
              left: "-30%",
              width: "160%",
              height: "160%",
              backgroundImage: "radial-gradient(circle, rgba(201, 162, 39, 0.12) 0%, transparent 65%)",
              animation: "pulseGlow 6s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />

          {/* Top Logo Presentation */}
          <Box zIndex={1}>
            <Box display="flex" alignItems="center" gap={2.5} mb={4}>
              {/* Premium Logo Ring Container */}
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: "16px",
                  bgcolor: "rgba(255, 255, 255, 0.04)",
                  border: "1.5px solid rgba(201, 162, 39, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1.1,
                  boxShadow: "0 10px 25px -5px rgba(201, 162, 39, 0.2)",
                  transition: "transform 300ms ease, border-color 300ms ease, box-shadow 300ms ease",
                  animation: "floatSlow 5s ease-in-out infinite",
                  "&:hover": {
                    transform: "scale(1.04)",
                    borderColor: brand.brassLight,
                    boxShadow: "0 12px 30px -4px rgba(201, 162, 39, 0.35)",
                  },
                }}
              >
                <img
                  src={loginicon}
                  alt="OurGenie Logo"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", serif',
                    fontWeight: 700,
                    fontSize: "1.35rem",
                    color: brand.parchment,
                    letterSpacing: "0.04em",
                    lineHeight: 1.1,
                  }}
                >
                  OURGENIE
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: brand.brass,
                    letterSpacing: "0.18em",
                    mt: 0.3,
                  }}
                >
                  COMMAND PORTAL
                </Typography>
              </Box>
            </Box>

            {/* Tagline */}
            <Typography
              sx={{
                color: brand.parchment,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                fontWeight: 500,
                lineHeight: 1.5,
                mb: 4,
                opacity: 0.9,
              }}
            >
              “Intelligent operations. Secure control. Unified management.”
            </Typography>

            {/* Feature Indicators */}
            <Box display="flex" flexDirection="column" gap={1.8}>
              {[
                { icon: <MdVerifiedUser size={16} />, label: "Secure Access", desc: "Role-based administrative control" },
                { icon: <MdSpeed size={16} />, label: "Real-Time Operations", desc: "Live dispatch & booking feeds" },
                { icon: <MdHub size={16} />, label: "Centralized Control", desc: "Unified service & customer suite" },
              ].map((feat, i) => (
                <Box
                  key={i}
                  display="flex"
                  alignItems="center"
                  gap={1.8}
                  p={1.2}
                  px={1.8}
                  borderRadius="14px"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    transition: "all 200ms ease",
                    "&:hover": {
                      bgcolor: "rgba(201, 162, 39, 0.08)",
                      borderColor: "rgba(201, 162, 39, 0.25)",
                      transform: "translateX(3px)",
                    },
                  }}
                >
                  <Box sx={{ color: brand.brass, display: "flex", alignItems: "center" }}>
                    {feat.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: brand.parchment, fontSize: "0.82rem", fontWeight: 700 }}>
                      {feat.label}
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.72rem" }}>
                      {feat.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Bottom Security Status Badge */}
          <Box zIndex={1} mt={4} pt={2} borderTop="1px solid rgba(255, 255, 255, 0.06)" display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#2F9E68",
                  boxShadow: "0 0 10px #2F9E68",
                }}
              />
              <Typography sx={{ color: "#2F9E68", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em" }}>
                SECURE ENVIRONMENT
              </Typography>
            </Box>

            <Typography sx={{ color: brand.slate, fontSize: "0.7rem", fontFamily: '"IBM Plex Mono", monospace' }}>
              v2.4 Enterprise
            </Typography>
          </Box>
        </Box>

        {/* Right Section: Premium Authentication Console */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 4, sm: 5, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.98)",
          }}
        >
          {/* Section Typography Hierarchy */}
          <Box mb={4}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: brand.brass, letterSpacing: "0.15em", uppercase: true }}>
              Welcome back
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: brand.ink950,
                fontSize: { xs: "1.6rem", md: "1.85rem" },
                mt: 0.5,
              }}
            >
              Sign in to your account
            </Typography>
            <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
              Access your secure OurGenie management workspace.
            </Typography>
          </Box>

          {/* Form Console */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Email / Mobile Field */}
            <Box mb={2.5}>
              <Typography sx={{ color: brand.ink950, fontSize: "0.8rem", fontWeight: 700, mb: 0.8 }}>
                Email or Phone Number
              </Typography>
              <TextField
                fullWidth
                placeholder="name@ourgenie.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    bgcolor: brand.paper,
                    fontSize: "0.9rem",
                    transition: "all 200ms ease",
                    "& fieldset": { borderColor: `${brand.slateLight}60` },
                    "&:hover fieldset": { borderColor: brand.slate },
                    "&.Mui-focused fieldset": {
                      borderColor: brand.brass,
                      borderWidth: "1.5px",
                      boxShadow: "0 0 12px rgba(201, 162, 39, 0.15)",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdEmail color={brand.slate} size={18} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Password Field */}
            <Box mb={3.5}>
              <Typography sx={{ color: brand.ink950, fontSize: "0.8rem", fontWeight: 700, mb: 0.8 }}>
                Account Password
              </Typography>
              <TextField
                fullWidth
                placeholder="••••••••••••"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    bgcolor: brand.paper,
                    fontSize: "0.9rem",
                    transition: "all 200ms ease",
                    "& fieldset": { borderColor: `${brand.slateLight}60` },
                    "&:hover fieldset": { borderColor: brand.slate },
                    "&.Mui-focused fieldset": {
                      borderColor: brand.brass,
                      borderWidth: "1.5px",
                      boxShadow: "0 0 12px rgba(201, 162, 39, 0.15)",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdLock color={brand.slate} size={18} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        tabIndex={-1}
                        size="small"
                        sx={{ color: brand.slate }}
                      >
                        {showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Submit Action Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              endIcon={!loading && <MdArrowForward size={18} />}
              sx={{
                py: 1.5,
                borderRadius: "14px",
                fontWeight: 700,
                fontSize: "0.92rem",
                textTransform: "none",
                bgcolor: brand.brass,
                color: brand.ink950,
                boxShadow: "0 8px 25px -6px rgba(201,162,39,0.5)",
                transition: "all 250ms ease",
                "&:hover": {
                  bgcolor: brand.brassLight,
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 30px -4px rgba(201,162,39,0.6)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {loading ? (
                <Box display="flex" alignItems="center" gap={1.2}>
                  <CircularProgress size={18} color="inherit" />
                  <span>Authenticating...</span>
                </Box>
              ) : (
                "Authenticate Session"
              )}
            </Button>
          </Box>

          {/* Security Footer Line */}
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} mt={4} pt={2.5} borderTop={`1px solid ${brand.slateLight}30`}>
            <MdSecurity color={brand.slate} size={14} />
            <Typography sx={{ color: brand.slate, fontSize: "0.75rem", fontWeight: 600 }}>
              Protected by 256-bit encrypted session control
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
